/**
 * Changing a live SIP's instalment amount, on an investor's behalf.
 *
 * A HIGHER AMOUNT IS TWO INSTRUCTIONS. FP models it as a mandate swap plus an
 * amount change, and each settles independently — the mandate half can land
 * while the amount half fails, leaving the SIP running on a new mandate at the
 * old amount. Both statuses are therefore returned, and the caller reports both
 * rather than collapsing them into one verdict.
 *
 * ONLY A HIGHER AMOUNT RAISES A REPLACEMENT MANDATE. The existing mandate's
 * approved limit is a ceiling the bank set, so FP does not edit it; a higher
 * instalment needs a new e-mandate, authorised at the bank, and that costs one
 * of the five mandate changes a SIP is allowed whether or not the amount half
 * then succeeds. A LOWER amount already fits under the existing mandate, so it
 * runs the amount instruction alone — no bank step, no change spent. This is
 * the user platform's split (beginModification / beginAmountReduction).
 *
 * Sequences (the user platform's, unchanged):
 *
 *   higher   mf_mandates/create/                                  new e-mandate
 *            mf_mandates/emandate/auth/                           bank authorisation
 *            → popup → /mf-payment-return → back here
 *            mf_mandates/list/sync/?mandate_id=                   poll until APPROVED
 *            mf_plan_mandate_modification_instructions/create/    + .../complete/
 *            mf_plan_amount_modification_instructions/create/     + .../complete/
 *
 *   lower    mf_plan_amount_modification_instructions/create/     + .../complete/
 *
 * NO CONSENT OTP: the admin acts directly on the investor's behalf. The bank's
 * e-mandate authorisation is not part of that layer and is preserved.
 */

import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "./api";
import type { MFRecord } from "./mfAdminInvestments";
import { awaitPopupReturn, popupReturnUrl } from "../Helpers/mfPopupReturn";

/** FP rate-limits these; the user platform paces them the same way. */
const STEP_DELAY_MS = 1200;
const APPROVAL_POLL_MS = 4000;
const APPROVAL_TIMEOUT_MS = 15 * 60 * 1000;

const pause = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const isRecord = (value: any): value is MFRecord =>
  !!value && typeof value === "object" && !Array.isArray(value);

const payloadOf = (response: any): any => response?.data ?? response;

const messageFrom = (payload: any): string => {
  if (typeof payload === "string") return payload.trim();
  if (Array.isArray(payload)) return payload.map(messageFrom).filter(Boolean).join(" ");
  if (!isRecord(payload)) return "";
  for (const key of [
    "non_field_errors",
    "failure_reason",
    "reason",
    "detail",
    "message",
    "error",
  ]) {
    if (!(key in payload)) continue;
    const message = messageFrom(payload[key]);
    if (message) return message;
  }
  return "";
};

export const getMFModificationError = (error: any, fallback: string): string =>
  messageFrom(error?.response?.data) || error?.message || fallback;

const assertOk = (payload: any, fallback: string) => {
  if (isRecord(payload) && payload.success === false) {
    throw new Error(messageFrom(payload) || fallback);
  }
  return payload;
};

const NORMALISED = (value: any) =>
  String(value || "").trim().toUpperCase().replace(/[\s-]+/g, "_");

const COMPLETED_STATES = ["COMPLETED", "COMPLETE", "SUCCESS", "SUCCEEDED", "DONE"];
const FAILED_STATES = ["FAILED", "FAILURE", "REJECTED", "CANCELLED", "EXPIRED"];

export const isModificationCompleted = (value: any) =>
  COMPLETED_STATES.indexOf(NORMALISED(value)) >= 0;
export const isModificationFailed = (value: any) =>
  FAILED_STATES.indexOf(NORMALISED(value)) >= 0;

/**
 * Whether an instruction has finished, one way or the other. Anything else is
 * still in flight. The user platform's list (GoalsSipsTab) plus REVERSED, and
 * NOT_REQUIRED — a half the change never needed can never finish, and must
 * not hold the SIP locked forever.
 */
const isModificationSettled = (value: any) =>
  isModificationCompleted(value) ||
  isModificationFailed(value) ||
  ["REVERSED", "NOT_REQUIRED"].indexOf(NORMALISED(value)) >= 0;

/** How many times a SIP's mandate may be replaced. */
export const MF_MANDATE_CHANGE_LIMIT = 5;

/**
 * Mandate changes still available on a SIP, from its `mandate_update_count`;
 * null when the record does not say, so the caller never invents a number.
 */
export const mfMandateChangesRemaining = (order: MFRecord): number | null => {
  const used = Number(order.mandate_update_count);
  if (order.mandate_update_count == null || !Number.isFinite(used)) return null;
  return Math.max(0, MF_MANDATE_CHANGE_LIMIT - Math.max(0, used));
};

/** The user platform's wording for an increase with no mandate changes left. */
export const MF_MANDATE_CHANGES_EXHAUSTED_REASON =
  "All five mandate changes have been used. You can still lower the SIP amount using the existing mandate.";

export const MF_MODIFICATION_PENDING_REASON =
  "A modification is already pending on this SIP. Wait for it to finish before starting another.";

/**
 * Whether a change to this SIP is still in flight.
 *
 * Starting another would raise a second replacement mandate — each one spends
 * one of the five mandate changes a SIP is allowed — and race the first. The
 * user platform blocks it the same way. An instruction with no state at all is
 * not counted: nothing could ever clear it.
 */
export const hasPendingModification = (
  instructions: MFRecord[],
  purchasePlanId: string
): boolean =>
  instructions.some((instruction) => {
    if (String(instruction.plan || "") !== purchasePlanId) return false;
    const state = instruction.state || instruction.status;
    return Boolean(String(state || "").trim()) && !isModificationSettled(state);
  });

export interface MFModificationResult {
  mandateStatus: string;
  mandateFailureReason: string;
  amountStatus: string;
  amountFailureReason: string;
  mandateId: string;
}

export type MFModificationReporter = (message: string) => void;

/** The instruction uuid the create step returns, under any of its spellings. */
const requireModificationUuid = (payload: any, label: string): string => {
  const record = isRecord(payload?.data) ? payload.data : payload;
  const uuid =
    record?.plan_modification_uuid ||
    record?.uuid ||
    record?.id ||
    (isRecord(record?.instruction) ? record.instruction.uuid : "");
  const value = String(uuid || "").trim();
  if (!value) {
    throw new Error(`The ${label} instruction did not return an identifier.`);
  }
  return value;
};

const instructionState = (payload: any): { state: string; reason: string } => {
  const record = isRecord(payload?.data) ? payload.data : payload;
  return {
    state: String(record?.state || record?.status || "").trim(),
    reason: String(record?.failure_reason || record?.reason || "").trim(),
  };
};

/**
 * Every modification instruction on an investor's SIPs.
 *
 * Keyed by the investor's user uuid, as the admin API list and the user
 * platform both call it — not by plan. To see one SIP's instructions, filter
 * the result on `plan`. No history reads as a 404, which is not a failure.
 *
 * Live response (24-09-2026): `{ success, message, data: [ instruction ] }`,
 * each instruction with `id`, `plan`, `state`, `amount` / `payment_source`.
 * Only records that are instructions are kept — a `plan` and a `state` — as
 * the user platform does, so an empty or odd `data` never becomes a row.
 */
const isInstruction = (value: any): value is MFRecord =>
  isRecord(value) &&
  typeof value.plan === "string" &&
  value.plan.trim() !== "" &&
  typeof (value.state || value.status) === "string";

export const fetchMFPlanModifications = async (
  userUuid: string
): Promise<MFRecord[]> => {
  let response: any;
  try {
    response = await makeGETRequestOnUserService(
      `/mutual_fund/mf_plan_modification_instructions/get/${encodeURIComponent(userUuid)}/`
    );
  } catch (error) {
    if ((error as any)?.response?.status === 404) return [];
    throw error;
  }
  const payload = payloadOf(response);
  const data = isRecord(payload) ? payload.data : null;
  if (Array.isArray(data)) return data.filter(isInstruction);
  if (Array.isArray(payload)) return payload.filter(isInstruction);
  return isInstruction(data) ? [data] : [];
};

/**
 * The amount half. Safe to run on its own once the mandate half has completed.
 */
export const completeMFAmountModification = async (input: {
  purchasePlanId: string;
  userUuid: string;
  amount: number;
  report: MFModificationReporter;
}): Promise<{ status: string; failureReason: string }> => {
  const { purchasePlanId, userUuid, amount, report } = input;

  report("Preparing the new instalment amount…");
  const created = assertOk(
    payloadOf(
      await makePOSTRequestOnUserService(
        "/mutual_fund/mf_plan_amount_modification_instructions/create/",
        { purchase_plan: purchasePlanId, user_uuid: userUuid, amount }
      )
    ),
    "The amount modification could not be prepared."
  );
  const uuid = requireModificationUuid(created, "amount modification");

  await pause(STEP_DELAY_MS);
  report("Applying the new instalment amount…");
  const completed = assertOk(
    payloadOf(
      await makePOSTRequestOnUserService(
        "/mutual_fund/mf_plan_amount_modification_instructions_complete/",
        {
          purchase_plan: purchasePlanId,
          user_uuid: userUuid,
          plan_modification_uuid: uuid,
          amount,
        }
      )
    ),
    "The amount modification could not be applied."
  );
  const settled = instructionState(completed);
  return {
    status: completed?.amount_status || settled.state || "unknown",
    failureReason: completed?.amount_failure_reason || settled.reason || "",
  };
};

/**
 * A lower instalment. It fits under the existing mandate's limit, so only the
 * amount instruction runs: no replacement mandate, no bank authorisation, and
 * none of the five mandate changes spent. The mandate half is reported as
 * NOT_REQUIRED, as the user platform records it.
 */
export const lowerMFSipAmount = async (input: {
  purchasePlanId: string;
  userUuid: string;
  amount: number;
  report: MFModificationReporter;
}): Promise<MFModificationResult> => {
  const amount = await completeMFAmountModification(input);
  return {
    mandateStatus: "NOT_REQUIRED",
    mandateFailureReason: "",
    amountStatus: amount.status,
    amountFailureReason: amount.failureReason,
    mandateId: "",
  };
};

export interface MFModificationInput {
  purchasePlanId: string;
  userUuid: string;
  amount: number;
  /** The mandate the SIP runs on today — the replacement must differ from it. */
  currentMandateId?: string | null;
  returnPath: string;
}

/**
 * The whole modification: replacement mandate, bank authorisation, then both
 * instruction pairs.
 *
 * The popup is handed in already open, because by the time this runs the click
 * that authorised it is over and a blocker would refuse a fresh one.
 */
export const modifyMFSipAmount = async (
  input: MFModificationInput,
  popup: Window,
  report: MFModificationReporter
): Promise<MFModificationResult> => {
  const { purchasePlanId, userUuid, amount, currentMandateId, returnPath } = input;

  report("Creating a replacement e-mandate…");
  const mandate = assertOk(
    payloadOf(
      await makePOSTRequestOnUserService("/mutual_fund/mf_mandates/create/", {
        user_uuid: userUuid,
        mandate_type: "E_MANDATE",
        mandate_limit: amount,
      })
    ),
    "The replacement e-mandate could not be created."
  );
  const mandateId = String(mandate?.mandate_id || mandate?.id || "").trim();
  if (!mandateId || (currentMandateId && mandateId === String(currentMandateId))) {
    throw new Error("A valid replacement mandate was not returned.");
  }

  await pause(STEP_DELAY_MS);
  report("Opening bank authorisation…");
  const auth = assertOk(
    payloadOf(
      await makePOSTRequestOnUserService("/mutual_fund/mf_mandates/emandate/auth/", {
        mandate_id: mandateId,
        payment_postback_url: popupReturnUrl(returnPath),
      })
    ),
    "The bank authorisation could not be started."
  );
  const authUrl = String(
    auth?.token_url || auth?.redirect_url || auth?.payment_url || auth?.url || ""
  ).trim();
  if (!authUrl) {
    throw new Error(
      `E-mandate ${mandateId} was created, but the bank authorisation link was not returned. The SIP is unchanged.`
    );
  }
  if (!/^https:/i.test(authUrl)) {
    throw new Error("The bank returned an invalid authorisation link.");
  }

  popup.location.href = authUrl;
  report("Waiting for bank authorisation…");
  await awaitPopupReturn(popup);

  /* ── approval is not instant; poll the mandate until the bank settles it ── */
  report("Waiting for the bank to approve the mandate…");
  const deadline = Date.now() + APPROVAL_TIMEOUT_MS;
  let approved = false;
  while (Date.now() < deadline) {
    const synced = payloadOf(
      await makeGETRequestOnUserService(
        `/mutual_fund/mf_mandates/list/sync/?mandate_id=${encodeURIComponent(mandateId)}`
      )
    );
    const record = isRecord(synced?.data) ? synced.data : synced;
    const state = NORMALISED(record?.mandate_status || record?.status || record?.state);
    if (FAILED_STATES.indexOf(state) >= 0) {
      throw new Error("The bank did not approve the replacement mandate. The SIP is unchanged.");
    }
    if (state === "APPROVED") {
      const approvedLimit = Number(record?.mandate_limit);
      if (!Number.isFinite(approvedLimit) || approvedLimit < amount) {
        throw new Error(
          "The approved mandate limit does not cover the new instalment. The SIP is unchanged."
        );
      }
      approved = true;
      break;
    }
    await pause(APPROVAL_POLL_MS);
  }
  if (!approved) {
    throw new Error(
      "Mandate approval is still pending. Refresh this investor's SIPs before starting another request."
    );
  }
  if (!popup.closed) popup.close();

  /* ── half one: point the SIP at the new mandate ── */
  report("Applying the replacement mandate to the SIP…");
  const mandateCreated = assertOk(
    payloadOf(
      await makePOSTRequestOnUserService(
        "/mutual_fund/mf_plan_mandate_modification_instructions/create/",
        {
          purchase_plan: purchasePlanId,
          user_uuid: userUuid,
          payment_method: "mandate",
          payment_source: mandateId,
        }
      )
    ),
    "The mandate modification could not be prepared."
  );
  const mandateUuid = requireModificationUuid(mandateCreated, "mandate modification");

  await pause(STEP_DELAY_MS);
  const mandateCompleted = assertOk(
    payloadOf(
      await makePOSTRequestOnUserService(
        "/mutual_fund/mf_plan_mandate_modification_instructions_complete/",
        {
          purchase_plan: purchasePlanId,
          user_uuid: userUuid,
          plan_modification_uuid: mandateUuid,
          payment_source: mandateId,
        }
      )
    ),
    "The mandate modification could not be applied."
  );
  const mandateSettled = instructionState(mandateCompleted);
  const mandateStatus =
    mandateCompleted?.mandate_status || mandateSettled.state || "unknown";
  const mandateFailureReason =
    mandateCompleted?.mandate_failure_reason || mandateSettled.reason || "";

  const result: MFModificationResult = {
    mandateId,
    mandateStatus,
    mandateFailureReason,
    amountStatus: "not_attempted",
    amountFailureReason:
      "The amount update was not started because the replacement mandate did not complete.",
  };

  /* ── half two: only meaningful once the mandate actually landed ── */
  if (!isModificationCompleted(mandateStatus)) return result;

  await pause(STEP_DELAY_MS);
  const amountResult = await completeMFAmountModification({
    purchasePlanId,
    userUuid,
    amount,
    report,
  });
  return {
    ...result,
    amountStatus: amountResult.status,
    amountFailureReason: amountResult.failureReason,
  };
};

/** Whether this SIP can be modified at all. */
export const mfModificationDisabledReason = (order: MFRecord): string => {
  if (!order.purchase_plan_id) return "This order has no purchase plan to modify.";
  if (String(order.state || "").trim().toLowerCase() !== "active") {
    return "Only an active SIP can be modified.";
  }
  if (order.cancellation_scheduled_on) {
    return "This SIP already has a cancellation scheduled.";
  }
  if (Number(order.remaining_installments) === 0) {
    return "This SIP has no remaining instalments.";
  }
  return "";
};
